// Forecast run health on the Data Pipeline page.
//
// A job-level status cannot express what this shows. A forecast run reaches
// status='success' while individual series fail and best-effort steps -- the total
// prediction band, MinT reconciliation, cold-start shrinkage -- silently produce
// nothing. For five days in July 2026 that looked exactly like a healthy run, because
// the only record was rows in forecast_series_failure that nothing read.
//
// So this panel is deliberately quiet when there is nothing wrong and loud when there
// is: it renders NOTHING for a clean forecast, rather than adding a green panel nobody
// needs to read.
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import PropTypes from "prop-types";
import Badge from "../../components/ui-components/Badge";
import { getForecastHealth } from "./api";
import { formatRelativeTime } from "./utils";

// The backend's writeJSON wraps the payload under a named key.
export function unwrapForecastRuns(payload) {
  const runs = payload?.forecast?.runs;
  return Array.isArray(runs) ? runs : [];
}

// A run is worth showing when it did not end cleanly OR ended "successfully" with
// series missing. Everything else stays hidden.
export function needsAttention(run) {
  if (!run) return false;
  if (run.status !== "success") return true;
  return (run.failure_count || 0) > 0;
}

const KIND_LABELS = {
  allocation_inputs: "Allocation inputs",
  model_selection: "Model selection",
  holdout: "Holdout evaluation",
  insights: "Cost insights",
  reconstruct: "Reconstruction",
};

export function kindLabel(kind) {
  return KIND_LABELS[kind] || kind;
}

// Series keys travel as JSON — ["cost_base", ["bq:project", "bigquery_workload"]] —
// because source keys legitimately contain colons and pipes. Render them readably but
// never throw on an unexpected shape: a malformed key must not blank the panel.
export function formatSeriesKey(seriesKey) {
  if (!seriesKey) return "(unknown series)";
  try {
    const parsed = JSON.parse(seriesKey);
    if (Array.isArray(parsed) && parsed.length === 2 && Array.isArray(parsed[1])) {
      return `${parsed[0]} · ${parsed[1].join(" / ")}`;
    }
    if (Array.isArray(parsed)) return parsed.join(" / ");
  } catch {
    // not JSON — fall through to the raw value
  }
  return String(seriesKey);
}

// The stored reason is "ExceptionType: message". The type is what distinguishes one
// class of failure from another at a glance.
export function reasonType(reason) {
  if (!reason) return "";
  const i = reason.indexOf(":");
  return i > 0 ? reason.slice(0, i) : reason;
}

function statusBadge(run) {
  if (run.status === "success") return { status: "warning", label: "Degraded" };
  if (run.status === "failed") return { status: "failed", label: "Failed" };
  return { status: "pending", label: run.status };
}

const RunRow = ({ run }) => {
  const [expanded, setExpanded] = useState(false);
  const badge = statusBadge(run);
  const failures = run.failures || [];
  const when = run.finished_at || run.started_at;

  return (
    <div className="border-t border-card-bd first:border-t-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-primary-soft"
        aria-expanded={expanded}
      >
        <FontAwesomeIcon
          icon={icon({ name: "chevron-right", family: "sharp", style: "solid" })}
          className={`text-text-2 text-xs ${expanded ? "rotate-90" : ""}`}
        />
        <span className="text-sm font-medium text-text min-w-[150px]">{kindLabel(run.kind)}</span>
        <Badge status={badge.status} label={badge.label} size="sm" />
        <span className="text-sm text-text-2">
          {run.failure_count > 0
            ? `${run.failure_count} of ${run.series_total} series failed`
            : `${run.series_done} of ${run.series_total} series`}
        </span>
        <span className="text-sm text-text-2 ml-auto">{when ? formatRelativeTime(when) : "—"}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-4 pl-11">
          {/* A run-level error is the reason the whole run failed; show it first. */}
          {run.error_message ? (
            <p className="text-sm text-danger mb-3 font-mono break-words">{run.error_message}</p>
          ) : null}

          {failures.length > 0 ? (
            <ul className="text-sm">
              {failures.map((f) => (
                <li key={`${f.series_key}-${f.reason}`} className="py-1.5 border-b border-card-bd last:border-b-0">
                  <span className="text-text">{formatSeriesKey(f.series_key)}</span>
                  {f.algorithm ? <span className="text-text-2"> · {f.algorithm}</span> : null}
                  <div className="text-text-2 font-mono text-xs break-words mt-0.5">{f.reason}</div>
                </li>
              ))}
              {run.failure_count > failures.length ? (
                <li className="pt-2 text-text-2">
                  … and {run.failure_count - failures.length} more
                </li>
              ) : null}
            </ul>
          ) : (
            !run.error_message && <p className="text-sm text-text-2">No per-series detail recorded.</p>
          )}
        </div>
      )}
    </div>
  );
};

RunRow.propTypes = { run: PropTypes.object.isRequired };

const ForecastHealthPanel = () => {
  const { data } = useQuery({
    queryKey: ["forecast-health"],
    queryFn: getForecastHealth,
    refetchInterval: 30000,
    // A failed request must not replace the page with an error: the pipeline view is
    // still useful without this panel.
    retry: false,
  });

  const runs = unwrapForecastRuns(data).filter(needsAttention);
  if (runs.length === 0) return null;

  return (
    <div className="bg-card rounded-card border border-card-bd mb-4 overflow-hidden">
      <div className="flex items-center gap-2 px-5 h-[36px] text-text-2">
        <FontAwesomeIcon
          icon={icon({ name: "triangle-exclamation", family: "sharp", style: "solid" })}
          className="text-warning text-xs"
        />
        <span className="uppercase text-xs tracking-wide font-medium">Forecast errors</span>
      </div>
      {runs.map((run) => (
        <RunRow key={run.run_id} run={run} />
      ))}
    </div>
  );
};

export default ForecastHealthPanel;
